import { fetchWithSupabaseAuth } from "./supabaseClient.js";
import { serializeDispatchAlertCaptureContext } from "./dispatchAlertCaptureContext.js";

const POLICY_URL = "/api/me/diagnostics/alert-capture";
const CAPTURE_UPLOAD_URL = "/api/me/diagnostics/alert-captures";

async function readErrorDetails(response, fallbackMessage) {
  const payload = await response.json().catch(() => null);
  const detailObject = payload?.detail && typeof payload.detail === "object"
    ? payload.detail
    : null;
  const detail = typeof payload?.detail === "string"
    ? payload.detail.trim()
    : typeof detailObject?.message === "string"
      ? detailObject.message.trim()
      : "";
  const message = typeof payload?.message === "string" ? payload.message.trim() : "";
  const code = typeof detailObject?.code === "string"
    ? detailObject.code.trim()
    : typeof payload?.code === "string"
      ? payload.code.trim()
      : "";
  return {
    code,
    message: detail || message || fallbackMessage,
  };
}

async function throwResponseError(response, fallbackMessage) {
  const { code, message } = await readErrorDetails(response, fallbackMessage);
  const error = new Error(message);
  error.status = response.status;
  if (code) {
    error.code = code;
  }
  throw error;
}

export async function fetchDispatchAlertCapturePolicy({
  etag = "",
  signal = undefined,
} = {}) {
  const headers = {};
  if (typeof etag === "string" && etag.trim()) {
    headers["If-None-Match"] = etag.trim();
  }

  const response = await fetchWithSupabaseAuth(POLICY_URL, {
    headers,
    signal,
  });

  if (response.status === 304) {
    return {
      etag: response.headers.get("ETag") || etag || "",
      notModified: true,
      policy: null,
    };
  }

  if (!response.ok) {
    await throwResponseError(
      response,
      "Impossible de vérifier la politique de capture des alertes.",
    );
  }

  return {
    etag: response.headers.get("ETag") || "",
    notModified: false,
    policy: await response.json(),
  };
}

export async function uploadDispatchAlertCapture({
  alertText = "",
  alertType,
  campaignId,
  captureContext = null,
  clientCaptureId,
  dispatchDate = "",
  dispatchPeriod = "",
  image,
  occurredAt,
  signal = undefined,
  sourcePath,
}) {
  const body = new FormData();
  body.append("image", image, `dispatch-alert-${clientCaptureId}.png`);
  body.append("client_capture_id", clientCaptureId);
  body.append("campaign_id", campaignId);
  body.append("alert_type", alertType);
  body.append("occurred_at", occurredAt);
  body.append("source_path", sourcePath);

  if (dispatchDate) {
    body.append("dispatch_date", dispatchDate);
  }
  if (dispatchPeriod) {
    body.append("dispatch_period", dispatchPeriod);
  }
  if (alertText) {
    body.append("alert_text", alertText);
  }
  const serializedCaptureContext = serializeDispatchAlertCaptureContext(captureContext);
  if (serializedCaptureContext) {
    body.append("capture_context", serializedCaptureContext);
  }

  const response = await fetchWithSupabaseAuth(CAPTURE_UPLOAD_URL, {
    body,
    method: "POST",
    signal,
  });

  if (!response.ok) {
    await throwResponseError(
      response,
      "Impossible d'enregistrer la capture de l'alerte.",
    );
  }

  return response.json();
}
